/**
 * Feedback modal component for BSP platform suggestions
 */

import { useState } from 'react';
import { Modal, Form, Input, Select, Button, message, Spin } from 'antd';
import { useAuthStore } from '../store/authStore';
import { useT } from '@/i18n';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

type FeedbackType = 'Feature' | 'Bug' | 'Optimize' | 'Others';

const FeedbackModal: React.FC<FeedbackModalProps> = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const { t } = useT();
  const [loading, setLoading] = useState(false);
  const { user } = useAuthStore();

  const handleSubmit = async (values: {
    type: FeedbackType;
    subject: string;
    description: string;
  }) => {
    setLoading(true);
    try {
      // Submit feedback to backend
      const response = await fetch('/api/v1/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: user?.username || user?.display_name || 'Anonymous',
          feedback_type: values.type,
          subject: values.subject,
          description: values.description,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      message.success(t('感谢您的反馈！'));
      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Feedback submission error:', error);
      message.error(t('反馈提交失败，请稍后重试'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t('提交反馈')}
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={500}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            label={t('反馈类型')}
            name="type"
            rules={[{ required: true, message: t('请选择反馈类型') }]}
          >
            <Select
              placeholder={t('选择反馈类型')}
              options={[
                { label: t('新功能'), value: 'Feature' },
                { label: t('缺陷'), value: 'Bug' },
                { label: t('优化'), value: 'Optimize' },
                { label: t('其他'), value: 'Others' },
              ]}
            />
          </Form.Item>

          <Form.Item
            label={t('主题')}
            name="subject"
            rules={[
              { required: true, message: t('请输入反馈主题') },
              { max: 100, message: t('主题长度不超过100字符') },
            ]}
          >
            <Input placeholder={t('简洁的反馈主题')} maxLength={100} />
          </Form.Item>

          <Form.Item
            label={t('详细描述')}
            name="description"
            rules={[
              { required: true, message: t('请输入反馈描述') },
              { max: 1000, message: t('描述长度不超过1000字符') },
            ]}
          >
            <Input.TextArea
              placeholder={t('详细描述您的反馈内容')}
              rows={5}
              maxLength={1000}
              showCount
            />
          </Form.Item>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel}>{t('取消')}</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {t('提交反馈')}
            </Button>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

export default FeedbackModal;
